import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bot, MessageCircle, X } from 'lucide-react';
import TomAssistantTrigger from './TomAssistantTrigger';
import { isAxiosError } from 'axios';
import { apiClient } from '../lib/api';
import { useScreenData } from '../hooks/useScreenData';
import { useAuth } from '../hooks/useAuth';
import { getTomRouteLabel } from '../lib/tomRouteContext';
import { tomTtsModel, tomTtsVoice } from '../lib/tomTtsDefaults';

interface TomAssistantProps {
  currentContext?: string;
  onStatusChange?: (status: Status) => void;
}

type Status = 'idle' | 'recording' | 'thinking' | 'speaking' | 'processing';

interface Message {
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
}

export default function TomAssistant({ currentContext, onStatusChange }: TomAssistantProps) {
  const { user } = useAuth();
  const [status, setStatus] = useState<Status>('idle');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  // Screen Data Hook
  const { screenData, isLoading: screenDataLoading, error: screenDataError } = useScreenData();
  
  const mediaRec = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const updateStatus = useCallback((newStatus: Status) => {
    console.log('📊 Tom: Status-Update:', status, '->', newStatus);
    setStatus(newStatus);
    onStatusChange?.(newStatus);
  }, [onStatusChange, status]);

  const startRecording = useCallback(async () => {
    try {
      console.log('🎤 Tom: Starte Mikrofon-Aufnahme');
      setError(null);
      
      // Mikrofon-Zugriff anfordern
      console.log('🎤 Tom: Fordere Mikrofon-Zugriff an');
      stream.current = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        } 
      });
      
      console.log('✅ Tom: Mikrofon-Zugriff erfolgreich');
      
      // MediaRecorder konfigurieren
      const options = {
        mimeType: 'audio/webm;codecs=opus'
      };
      
      console.log('🎤 Tom: Erstelle MediaRecorder mit Optionen:', options);
      mediaRec.current = new MediaRecorder(stream.current, options);
      chunks.current = [];

      mediaRec.current.ondataavailable = (event) => {
        console.log('📊 Tom: Audio-Daten empfangen', {
          size: event.data.size,
          type: event.data.type
        });
        if (event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };

      mediaRec.current.onstart = () => {
        console.log('▶️ Tom: MediaRecorder gestartet');
      };

      mediaRec.current.onstop = () => {
        console.log('⏹️ Tom: MediaRecorder gestoppt');
        // handleRecordingFinished wird über useEffect gesetzt
      };

      mediaRec.current.onerror = (error) => {
        console.error('❌ Tom: MediaRecorder Fehler:', error);
        setError('Aufnahme-Fehler. Bitte versuchen Sie es erneut.');
        updateStatus('idle');
      };

      console.log('🚀 Tom: Starte Aufnahme');
      mediaRec.current.start();
      updateStatus('recording');
      
    } catch (error) {
      console.error('❌ Tom: Recording failed:', error);
      setError('Mikrofon-Zugriff verweigert. Bitte erlauben Sie den Zugriff auf Ihr Mikrofon.');
      updateStatus('idle');
    }
  }, [updateStatus]);

  const stopRecording = useCallback(() => {
    console.log('⏹️ Tom: Stoppe Aufnahme');
    if (mediaRec.current?.state === 'recording') {
      console.log('⏹️ Tom: MediaRecorder ist aktiv, stoppe...');
      mediaRec.current.stop();
    } else {
      console.log('⚠️ Tom: MediaRecorder ist nicht aktiv, Status:', mediaRec.current?.state);
    }
  }, []);

  const handleRecordingFinished = useCallback(async () => {
    const blob = new Blob(chunks.current, { type: 'audio/webm' });
    cleanupStream();
    
    console.log('🎤 Tom: Audio-Aufnahme beendet', {
      blobSize: blob.size,
      blobType: blob.type,
      chunks: chunks.current.length
    });
    
    if (blob.size === 0) {
      console.log('⚠️ Tom: Leere Audio-Aufnahme, Abbruch');
      updateStatus('idle');
      return;
    }

    // Überbrückungssound direkt nach Aufnahme starten
    console.log('🎵 Tom: Starte Überbrückungssound nach Aufnahme');
    const crackleAudio = new Audio('/src/assets/recordcrackle.mp3');
    crackleAudio.volume = 0.3; // Leiser als Hauptaudio
    crackleAudio.loop = true;
    
    try {
      await crackleAudio.play();
      console.log('✅ Tom: Überbrückungssound gestartet');
    } catch (crackleError) {
      console.log('⚠️ Tom: Überbrückungssound konnte nicht abgespielt werden:', crackleError);
    }

    try {
      // Speech-to-Text
      console.log('🚀 Tom: Starte Speech-to-Text Verarbeitung');
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');

      const sttStartTime = Date.now();
      const sttResponse = await apiClient.post('/stt', formData, {
        timeout: 30000 // 30 Sekunden Timeout für STT (Content-Type setzt axios für FormData + Boundary)
      });
      const sttTime = Date.now() - sttStartTime;
      
      console.log('📊 Tom: STT-Verarbeitung abgeschlossen', {
        processingTime: sttTime,
        success: sttResponse.data.success,
        requestId: sttResponse.data.requestId
      });

      if (!sttResponse.data.success || !sttResponse.data.text) {
        console.log('❌ Tom: STT fehlgeschlagen', sttResponse.data);
        crackleAudio.pause();
        crackleAudio.currentTime = 0;
        setError('Spracherkennung fehlgeschlagen. Bitte versuchen Sie es erneut.');
        updateStatus('idle');
        return;
      }

      const userText = sttResponse.data.text;
      console.log('💬 Tom: Transkribierter Text', {
        text: userText,
        length: userText.length
      });
      
      const userMessage: Message = {
        role: 'user',
        text: userText,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, userMessage]);

      // AI-Assistant mit Enhanced Context aufrufen
      console.log('🤖 Tom: Starte Enhanced Context AI-Assistant Verarbeitung');
      updateStatus('thinking');
      
      // Immer aktuelle URL + Label mitschicken (auch wenn Screen-Data noch lädt oder „unknown“ ist)
      const navigation = {
        pathname: location.pathname,
        search: location.search || '',
        routeLabel: getTomRouteLabel(location.pathname),
      };
      const screenPayload =
        screenData != null
          ? { ...screenData, navigation }
          : {
              type: 'generic',
              data: {},
              timestamp: new Date().toISOString(),
              navigation,
            };

      const enhancedContext = {
        userMsg: userText,
        screenData: screenPayload,
        pageContext: currentContext || '',
      };
      
      console.log('📊 Tom: Enhanced Context:', enhancedContext);
      console.log('📊 Tom: Screen Data verfügbar:', !!screenData);
      console.log('📊 Tom: Screen Data Loading:', screenDataLoading);
      console.log('📊 Tom: Screen Data Error:', screenDataError);

      const aiStartTime = Date.now();
      const aiResponse = await apiClient.post('/assistant', enhancedContext, {
        timeout: 30000 // 30 Sekunden Timeout für AI-Assistant
      });
      const aiTime = Date.now() - aiStartTime;
      
      console.log('📊 Tom: AI-Assistant Verarbeitung abgeschlossen', {
        processingTime: aiTime,
        success: aiResponse.data?.success,
        requestId: aiResponse.data?.requestId,
        status: aiResponse.status
      });

      let reply = 'Entschuldigung, ich konnte keine Antwort generieren.';
      
      if (aiResponse.data?.success) {
        reply = aiResponse.data.answer;
        console.log('💬 Tom: AI-Antwort erhalten', {
          reply: reply,
          length: reply.length,
          tokensUsed: aiResponse.data.tokens_used
        });
      } else if (aiResponse.status === 503) {
        const d = aiResponse.data as
          | { error?: string; hint?: string; details?: string }
          | undefined;
        const parts = [d?.error, d?.hint].filter(Boolean) as string[];
        if (import.meta.env.DEV && d?.details) parts.push(String(d.details));
        reply =
          parts.join(' ') ||
          'Tom ist derzeit nicht verfügbar (LLM-Hub). Bitte Hub-Status und Modellrichtlinie prüfen.';
        console.log('⚠️ Tom: AI-Service nicht verfügbar', aiResponse.data);
      } else {
        console.log('⚠️ Tom: AI-Response unerwartet', {
          status: aiResponse.status,
          data: aiResponse.data
        });
        reply = 'Entschuldigung, es gab ein Problem bei der Verarbeitung. Bitte versuchen Sie es erneut.';
      }
      
      const assistantMessage: Message = {
        role: 'assistant',
        text: reply,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, assistantMessage]);

      // Text-to-Speech (vereinfacht wie in Lernrakete2)
      console.log('🔊 Tom: Starte Text-to-Speech Verarbeitung');
      console.log('🔊 Tom: Nach setMessages - Reply:', reply);
      console.log('🔊 Tom: Reply-Länge:', reply.length);
      updateStatus('speaking');
      
      // Überbrückungssound stoppen
      if (crackleAudio) {
        crackleAudio.pause();
        crackleAudio.currentTime = 0;
        console.log('⏹️ Tom: Überbrückungssound gestoppt');
      }
      
      try {
        const uid = user?.id != null ? String(user.id) : '';
        const ttsUrl = `${apiClient.getBaseUrl()}/tts?text=${encodeURIComponent(reply)}&voice=${encodeURIComponent(
          tomTtsVoice()
        )}&model=${encodeURIComponent(tomTtsModel())}${
          uid ? `&hubUserId=${encodeURIComponent(uid)}` : ''
        }&v=${Date.now()}`;
        console.log('🔊 Tom: TTS-URL:', ttsUrl);
        
        const audioResp = await fetch(ttsUrl, { cache: 'no-store' });
        if (!audioResp.ok) throw new Error(`TTS status ${audioResp.status}`);
        
        const audioBlob = await audioResp.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        
        audio.onended = () => {
          console.log('✅ Tom: Audio-Wiedergabe beendet');
          URL.revokeObjectURL(audioUrl);
          updateStatus('idle');
        };
        
        audio.onerror = (error) => {
          console.error('❌ Tom: Audio-Wiedergabe Fehler:', error);
          URL.revokeObjectURL(audioUrl);
          // Fallback: Browser TTS
          fallbackTTS(reply);
        };
        
        console.log('▶️ Tom: Starte Audio-Wiedergabe');
        await audio.play();
        console.log('✅ Tom: TTS-Verarbeitung erfolgreich abgeschlossen');
      } catch (err) {
        console.error('❌ Tom: TTS-Fehler:', err);
        console.log('🔄 Tom: Verwende Fallback-TTS');
        fallbackTTS(reply);
      }

    } catch (error) {
      console.error('Tom processing error:', error);
      console.log('🔍 Tom: Fehler-Details:', {
        error: error,
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      if (crackleAudio) {
        crackleAudio.pause();
        crackleAudio.currentTime = 0;
      }
      let msg = 'Verbindungsfehler. Bitte versuchen Sie es erneut.';
      if (isAxiosError(error) && error.response?.data) {
        const d = error.response.data as { details?: string; error?: string; upstreamStatus?: number };
        if (d.upstreamStatus === 401 || String(d.details || '').includes('401')) {
          msg =
            'Spracherkennung: WestAI hat den API-Key abgelehnt (401). Bitte WESTAI_API_KEY im Hub-Core prüfen.';
        } else if (typeof d.details === 'string' && d.details.trim()) {
          msg = `Spracherkennung fehlgeschlagen: ${d.details.slice(0, 200)}`;
        }
      }
      setError(msg);
      updateStatus('idle');
    }
  }, [currentContext, updateStatus, screenData, location.pathname, location.search, user?.id]);


  const fallbackTTS = useCallback((text: string) => {
    console.log('🔄 Tom: Verwende Browser TTS (Fallback)');
    
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'de-DE';
      utterance.onend = () => {
        console.log('✅ Tom: Browser TTS beendet');
        updateStatus('idle');
      };
      utterance.onerror = (error) => {
        console.error('❌ Tom: Browser TTS Fehler:', error);
        updateStatus('idle');
      };
      
      console.log('🚀 Tom: Starte Browser TTS');
      window.speechSynthesis.speak(utterance);
    } else {
      console.log('❌ Tom: Browser TTS nicht unterstützt');
      updateStatus('idle');
    }
  }, [updateStatus]);

  const cleanupStream = useCallback(() => {
    console.log('🧹 Tom: Cleanup Stream');
    stream.current?.getTracks().forEach(track => track.stop());
    stream.current = null;
  }, []);

  const toggleRecording = () => {
    console.log('🔄 Tom: Toggle Recording - aktueller Status:', status);
    if (status === 'recording') {
      console.log('🔄 Tom: Stoppe Aufnahme');
      // Sofortiger Status-Wechsel für bessere UX
      updateStatus('processing');
      stopRecording();
    } else {
      console.log('🔄 Tom: Starte Aufnahme');
      startRecording();
    }
  };

  const clearMessages = () => {
    console.log('🧹 Tom: Clear Messages');
    setMessages([]);
    setError(null);
  };

  useEffect(() => {
    if (mediaRec.current) {
      console.log('🔧 Tom: Setze onstop Handler für MediaRecorder');
      mediaRec.current.onstop = () => {
        console.log('⏹️ Tom: MediaRecorder onstop Event ausgelöst');
        console.log('🔧 Tom: Rufe handleRecordingFinished auf...');
        handleRecordingFinished();
      };
    }
  }, [handleRecordingFinished]);

  useEffect(() => {
    return () => {
      console.log('🧹 Tom: Cleanup - stoppe MediaRecorder falls aktiv');
      if (mediaRec.current?.state !== 'inactive') {
        mediaRec.current?.stop();
      }
      cleanupStream();
    };
  }, [cleanupStream]);

  const getStatusText = () => {
    switch (status) {
      case 'idle': return 'Klicken zum Sprechen';
      case 'recording': return 'Aufnahme läuft...';
      case 'processing': return 'Verarbeitung...';
      case 'thinking': return 'Tom denkt nach...';
      case 'speaking': return 'Tom spricht...';
      default: return '';
    }
  };

  const isBusy = status === 'processing' || status === 'thinking' || status === 'speaking';

  return (
    <div className="relative">
      <TomAssistantTrigger
        onClick={toggleRecording}
        title={getStatusText()}
        ariaLabel={getStatusText()}
        disabled={isBusy}
        active={status === 'recording' || isBusy}
      />

      {/* Chat-Interface (erweiterbar) */}
      {isExpanded && (
        <div className="absolute top-12 right-0 bg-white rounded-lg shadow-lg p-4 w-80 max-h-96 overflow-hidden z-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bot size={20} className="text-blue-500" />
              <span className="font-semibold">Tom</span>
            </div>
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 transition-colors"
              title="Chat schließen"
            >
              <X size={16} />
            </button>
          </div>

          {/* Nachrichten */}
          <div className="space-y-2 max-h-64 overflow-y-auto mb-3">
            {messages.length === 0 ? (
              <div className="text-gray-500 text-sm text-center py-4">
                Noch keine Unterhaltung. Sprechen Sie mit Tom!
              </div>
            ) : (
              messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                      message.role === 'user'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {message.text}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Fehler-Anzeige */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Aktionen */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={clearMessages}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm transition-colors"
            >
              Löschen
            </button>
            <button
              type="button"
              onClick={toggleRecording}
              disabled={status === 'processing' || status === 'thinking' || status === 'speaking'}
              className="flex-1 px-3 py-2 rounded-lg text-sm transition-colors bg-blue-500 text-white hover:opacity-80 disabled:opacity-50"
            >
              {status === 'recording' ? 'Stoppen' : 'Sprechen'}
            </button>
          </div>
        </div>
      )}

      {/* Chat-Button */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className={`transition-colors ${
            isExpanded 
              ? 'text-blue-500 hover:text-blue-600' 
              : 'text-gray-400 hover:text-gray-600'
          }`}
          title={isExpanded ? "Chat ausblenden" : "Chat anzeigen"}
        >
          <MessageCircle size={16} />
        </button>
      </div>
    </div>
  );
}
